import '../styles/landing.css';
import { useNews } from '../utils/news';

const METHODS = [
  {
    icon: '🤖',
    title: 'Forestry AI Assistant',
    text: 'Interact with satellite-derived datasets, forest inventories, and monitoring products using natural language queries powered by a domain-specific AI assistant.',
  },
  {
    icon: '🪓',
    title: 'Clearcut Detection',
    text: 'Annual forest disturbance mapping using multi-source satellite imagery, tracking clearcut and harvest activities across diverse Forest Management Units from 2015 to present.',
  },
  {
    icon: '🌿',
    title: 'Biomass Estimation',
    text: 'Above-Ground Biomass (AGB) mapping derived from SAR and optical data, providing spatial estimates of forest carbon stocks across large landscapes.',
  },
  {
    icon: '🛰️',
    title: 'HLS Imagery',
    text: 'Harmonized Landsat and Sentinel-2 (HLS) imagery providing 30 m observations at high revisit frequency for consistent monitoring through time.',
  },
  {
    icon: '🌍',
    title: 'Planet Imagery',
    text: 'High-resolution Planet imagery enabling sub-10 m monitoring of forest disturbances, providing finer spatial detail for disturbance mapping and validation.',
  },
];

const TEAM_MEMBERS = [
  'Dr. Vasit Sagan (Principal Investigator)',
  'Dr. Derek Tesser (Senior Research Scientist)',
  'Dr. Felipe Lopes (AI Research Scientist)',
  'Nuerbiye Muhetaer (PhD Student)',
  'Mustafizur Rahaman (PhD Student)',
];

const PARTNERS = [
  {
    name: 'Natural Resources Defense Council (NRDC)',
    desc: 'Science, policy, law, and advocacy support for climate, public lands, and communities.',
  },
  {
    name: 'Wahkohtowin',
    desc: 'First Nations social enterprise supporting rights, culture, and stewardship across shared territories.',
  },
  {
    name: 'Planet Labs',
    desc: 'High-resolution commercial satellite imagery for recent-year forest monitoring.',
  },
  {
    name: 'NASA Earthdata',
    desc: 'Provider of HLS time-series products used for annual and multi-year analyses.',
  },
];

// News posts come from src/content/news/*.md (see utils/news.js).
const LANDING_NEWS_COUNT = 4;

function LandingPage({ onEnter, onOpenAbout, onOpenNews, onOpenDocumentation }) {
  const latestNews = useNews().slice(0, LANDING_NEWS_COUNT);

  return (
    <div className="landing">

      {/* ── Hero ─────────────────────────────────────────── */}
      <section className="landing-hero" id="overview">
        <div className="landing-hero-content">
          <a href="https://www.remotesensinglab.org" rel="noopener noreferrer">
            <img
              src="/rsl-logo-transparent.png"
              alt="Remote Sensing Lab"
              className="landing-logo"
            />
          </a>
          <p className="landing-subtitle">Remote Sensing Lab · Saint Louis University</p>
          <h1 className="landing-title">ForestTrace</h1>
          <p className="landing-tagline">
            Forest Monitoring, Mapping, and AI Insights
          </p>
          <p className="landing-description">
            An integrated platform for exploring forest dynamics through satellite imagery, interactive geospatial visualization, and an AI-powered forestry assistant. 
            Analyze disturbances, biomass patterns, and multi-sensor observations in a unified web experience.
          </p>
          <div className="landing-cta-group">
            <button className="landing-cta" onClick={onEnter}>
              Launch Application
            </button>
            <button
              className="landing-cta-secondary"
              onClick={() => document.getElementById('methodology')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Explore Methods
            </button>
          </div>
        </div>

        <div className="landing-video-frame">
          <div className="landing-video-bar">
            <span /><span /><span />
          </div>
          <video
            className="landing-video"
            src="https://pub-1448884f2a1d4c5bbbb92161520eab76.r2.dev/videos/hero-demo.mp4"
            autoPlay
            muted
            loop
            playsInline
          />
          <div className="landing-video-overlay">
            <p className="landing-video-overlay-title">Query the Forest</p>
            <p className="landing-video-overlay-body">
              ForestTrace turns decades of satellite and aerial imagery into
              queryable datasets, so you can find patterns, derive insights
              and act fast.
            </p>
          </div>
        </div>
      </section>

      {/* ── Methods ──────────────────────────────────────── */}
      <section className="landing-section" id="methodology">
        <p className="landing-section-title">Methodology</p>
        <h2 className="landing-section-heading">Platform Capabilities</h2>
        <div className="landing-cards">
          {METHODS.map(({ icon, title, text }) => (
            <div className="landing-card" key={title}>
              <span className="landing-card-icon">{icon}</span>
              <h3 className="landing-card-title">{title}</h3>
              <p className="landing-card-text">{text}</p>
            </div>
          ))}
        </div>
        <button className="landing-section-button" onClick={onOpenDocumentation}>
          Open Full Documentation
        </button>
      </section>

      {/* ── About ────────────────────────────────────────── */}
      <section className="landing-section" id="about">
        <p className="landing-section-title">About</p>
        <h2 className="landing-section-heading">Team and Institution</h2>
        <div className="landing-about">
          <div>
            <p className="landing-about-text">
              ForestTrace is developed by the{' '}
              <strong>Remote Sensing Lab (RSL)</strong> at{' '}
              <strong>Saint Louis University</strong>, a research group focused
              on the development and application of satellite remote sensing
              techniques for environmental monitoring and natural resource
              management.
            </p>
            <p className="landing-about-text">
              Our work integrates multi-source satellite data — including
              Landsat, Sentinel-2, SAR, and commercial high-resolution imagery
              — to characterize forest structure, disturbance dynamics, and
              carbon cycles at regional scales.
            </p>
            <p className="landing-about-text">
              This platform supports Indigenous-led forest stewardship and
              evidence-based land management decisions with an initial focus on 
              the boreal forests of northwestern Ontario, Canada.
            </p>
          </div>
          <div>
            <p className="landing-section-title" style={{ marginBottom: 16 }}>
              Research Team
            </p>
            <ul className="landing-team-list">
              {TEAM_MEMBERS.map((member) => (
                <li key={member}>{member}</li>
              ))}
            </ul>

            <p className="landing-section-title" style={{ marginBottom: 16, marginTop: 24 }}>
              Partners and Collaborators
            </p>
            <div className="landing-partners">
              {PARTNERS.map(({ name, desc }) => (
                <div className="landing-partner-badge" key={name}>
                  <strong>{name}</strong>
                  <span>{desc}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <button className="landing-section-button" onClick={onOpenAbout}>
          Read Full About Page
        </button>
      </section>

      {/* ── News ─────────────────────────────────────────── */}
      <section className="landing-section" id="news">
        <p className="landing-section-title">News</p>
        <h2 className="landing-section-heading">Latest Updates</h2>
        <div className="landing-cards">
          {latestNews.map(({ slug, tag, dateLabel, title, summary }) => (
            <article className="landing-card" key={slug}>
              <p className="landing-news-meta">{tag} · {dateLabel}</p>
              <h3 className="landing-card-title">{title}</h3>
              <p className="landing-card-text">{summary}</p>
              <button className="landing-news-more" onClick={() => onOpenNews(slug)}>
                Read more →
              </button>
            </article>
          ))}
        </div>
        <button className="landing-section-button" onClick={() => onOpenNews()}>
          View All News
        </button>
      </section>

      {/* ── Contact ──────────────────────────────────────── */}
      <section className="landing-section landing-contact-section" id="contact">
        <p className="landing-section-title">Contact</p>
        <h2 className="landing-section-heading">Get in Touch</h2>
        <p className="landing-about-text" style={{ marginBottom: 12 }}>
          For research inquiries and collaboration opportunities, contact Dr. Vasit Sagan.
        </p>
        <a className="landing-contact-link" href="mailto:vasit.sagan@slu.edu">
          vasit.sagan@slu.edu
        </a>
      </section>

      {/* ── Footer ───────────────────────────────────────── */}
      <footer className="landing-footer">
        © {new Date().getFullYear()} Remote Sensing Lab · Saint Louis University
      </footer>

    </div>
  );
}

export default LandingPage;
